// frontend/src/services/authService.js
import apiClient from './apiClient';
import { validateSecureConnection, clearSensitiveData } from '../utils/crypto';
import { mock } from '../mock/api';

// Cache user information in memory to avoid persisting sensitive data
let cachedUser = null;

/** Drop cached /auth/me so the next getCurrentUser() refetches (e.g. after tenant switch). */
export const clearUserCache = () => {
  cachedUser = null;
};

// CSRF: backend expects X-CSRF-Token header matching csrf_token cookie.
// apiClient will automatically include this header, but helper is provided for manual calls.
const getCsrfToken = () => {
  const match = document.cookie.match(/(^|; )csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : null;
};

// Get API URL from environment variable, with fallback
// Always use relative URLs to leverage the proxy and avoid CORS issues
const API_BASE = ''; // Use proxy for all environments

const useMockAuth = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true';

/**
 * Login user and get access token
 * SECURITY: Uses secure credential handling with JSON payload
 */
export const login = async (email, password) => {
  try {
    if (useMockAuth) {
      const normalized = String(email || '').trim().toLowerCase();
      const isAdmWes = normalized === 'admin@1wes.com' || normalized === 'viniciusaiires@hotmail.com';

      await mock.setSessionEmail({ email: normalized });

      // Determine profile/tenant by existing bindings in the mock DB.
      const state = await mock.getState();
      const tenants = state?.tenants || [];
      const bound = tenants.find((t) =>
        (t.users || []).some((u) => String(u.email || '').trim().toLowerCase() === normalized)
      );
      const boundUser = bound
        ? (bound.users || []).find((u) => String(u.email || '').trim().toLowerCase() === normalized)
        : null;

      const inferredProfile = (() => {
        if (isAdmWes) return 'adm_wes';
        if (boundUser?.type === 'adm_cliente') return 'adm_cliente';
        if (boundUser?.type === 'user_cliente') return 'user_cliente';
        // fallback heuristic
        const isAdmClienteHeuristic = normalized.includes('adm') || normalized.includes('admin');
        return isAdmClienteHeuristic ? 'adm_cliente' : 'user_cliente';
      })();

      const inferredTenantId = (() => {
        if (isAdmWes) return 'platform';
        if (bound?.id) return bound.id;
        // fallback: keep current tenantId as-is
        return undefined;
      })();

      await mock.setSession({ profile: inferredProfile, tenantId: inferredTenantId });
      cachedUser = await mock.me();
      if (cachedUser?.user_id) {
        localStorage.setItem('user_id', cachedUser.user_id);
      }
      return cachedUser;
    }

    // SECURITY: Validate secure connection in production
    validateSecureConnection();
    
    // SECURITY: Clear any existing cached data before login
    cachedUser = null;
    localStorage.removeItem('user_id');
    
    // Step 1: Get CSRF token first
    try {
      await apiClient.get('/auth/csrf-token');
    } catch (csrfError) {
      console.debug('CSRF token fetch failed, proceeding without it:', csrfError.message);
    }
    
    // Use regular login endpoint (working configuration)
    const loginData = {
      username: email,
      password: password
    };
    
    // SECURITY: Clear sensitive variables as soon as possible
    setTimeout(() => {
      clearSensitiveData({ email, password });
      // Also clear the loginData object
      loginData.password = null;
      delete loginData.password;
    }, 100);
    
    // SECURITY: Use apiClient - note: /auth/login expects form data, not JSON
    // Convert to form data for compatibility with OAuth2PasswordRequestForm
    const formData = new URLSearchParams();
    formData.append('username', loginData.username);
    formData.append('password', loginData.password);
    
    const response = await apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        // SECURITY: Add security headers
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Requested-With': 'XMLHttpRequest'
      },
      timeout: 10000, // 10 second timeout
      // SECURITY: Prevent credentials from being cached in browser
      cache: 'no-store'
    });
    
    // SECURITY: Tokens are stored as httpOnly cookies by the backend
    // Get user info using apiClient (cookies will be sent automatically)
    const userResponse = await apiClient.get('/auth/me');

    // Cache user in memory and store only minimal identifier
    cachedUser = userResponse.data;
    if (cachedUser?.user_id) {
      localStorage.setItem('user_id', cachedUser.user_id);
    }

    return cachedUser;
  } catch (error) {
    // SECURITY: Don't log sensitive error details
    console.error('Login failed');
    
    // SECURITY: Clear any sensitive data on error
    clearSensitiveData({ email, password });
    
    // Extract error details for better error messages (without exposing sensitive data)
    let errorMessage = 'Authentication failed';

    const responseData = error.response?.data;
    const detailFromJson = (() => {
      if (!responseData || typeof responseData !== 'object') return null;
      const d = responseData.detail;
      if (d == null) return null;
      if (Array.isArray(d)) {
        return d
          .map((x) => (typeof x === 'string' ? x : x?.msg))
          .filter(Boolean)
          .join(' ');
      }
      return String(d);
    })();

    if (error.response) {
      if (detailFromJson) {
        errorMessage = detailFromJson;
      } else if (typeof responseData === 'string' && responseData.trim()) {
        // Proxy dev server returns plain text e.g. "Proxy Error: connect ECONNREFUSED"
        errorMessage = responseData.includes('Proxy Error')
          ? 'Não foi possível conectar à API. Confirme se o backend Hub360 está rodando (ex.: uvicorn) na URL do REACT_APP_API_URL (padrão http://localhost:8004) e se o banco está acessível.'
          : (responseData.length > 300 ? 'Erro inesperado do servidor' : responseData);
      } else if (error.response.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response.status === 403) {
        errorMessage = 'Account locked or disabled';
      } else if (error.response.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if ([502, 503, 504].includes(error.response.status)) {
        errorMessage = 'Servidor da API indisponível. Verifique se o backend está em execução.';
      }
    } else if (error.message?.includes('Network Error')) {
      errorMessage = 'Unable to connect to authentication server';
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Logout user
 */
export const logout = async () => {
  try {
    if (useMockAuth) {
      cachedUser = null;
      localStorage.removeItem('user_id');
      return;
    }

    // Call backend logout endpoint to clear httpOnly cookies
    const csrf = getCsrfToken();
    await apiClient.post('/auth/logout', null, csrf ? {
      headers: {
        'X-CSRF-Token': csrf
      }
    } : {});
  } catch (error) {
    console.error('Logout error:', error);
    // Continue with local cleanup even if API call fails
  }

  // Clear cached data and identifier
  cachedUser = null;
  localStorage.removeItem('user_id');
};

/**
 * Refresh access token
 */
export const refreshToken = async () => {
  try {
    if (useMockAuth) {
      return { ok: true };
    }

    // Note: refresh is CSRF-exempted, apiClient will handle this automatically
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  } catch (error) {
    console.error('Token refresh error:', error);
    throw error;
  }
};

/**
 * Get current user data
 */
export const getCurrentUser = async () => {
  if (cachedUser) {
    return cachedUser;
  }

  try {
    if (useMockAuth) {
      cachedUser = await mock.me();
      if (cachedUser?.user_id) {
        localStorage.setItem('user_id', cachedUser.user_id);
      }
      return cachedUser;
    }

    const response = await apiClient.get('/auth/me', {
      timeout: 5000 // Shorter timeout for auth checks
    });
    cachedUser = response.data;
    if (cachedUser?.user_id) {
      localStorage.setItem('user_id', cachedUser.user_id);
    }
    return cachedUser;
  } catch (error) {
    // Don't log errors for expected 401 responses (not authenticated)
    if (error.response?.status !== 401) {
      console.error('Failed to get current user:', error);
    }
    throw error;
  }
};

export const setMockSession = async ({ profile, tenantId }) => {
  if (!useMockAuth) return null;
  await mock.setSession({ profile, tenantId });
  cachedUser = null;
  return getCurrentUser();
};

/**
 * Get current user from localStorage (synchronous)
 */
export const getCurrentUserSync = () => cachedUser;

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => !!cachedUser;

/**
 * Get user tenant ID
 */
export const getUserTenant = () => {
  return cachedUser?.tenant_id || null;
};

/**
 * Get user roles
 */
export const getUserRoles = () => {
  return cachedUser?.roles || [];
};

/**
 * Check if user has a specific role
 */
export const hasRole = (roleName) => {
  const roles = getUserRoles();
  return roles.includes(roleName);
};

/**
 * Get available OAuth providers
 */
export const getOAuthProviders = async () => {
  try {
    const response = await apiClient.get('/auth/oauth/providers');
    return response.data;
  } catch (error) {
    console.error('Failed to get OAuth providers:', error);
    return { providers: [], oauth_enabled: false };
  }
};

/**
 * Initiate OAuth login
 */
export const initiateOAuthLogin = (provider, tenantId = null) => {
  const params = new URLSearchParams();
  if (tenantId) {
    params.append('tenant_id', tenantId);
  }
  
  const url = `/auth/oauth/login/${provider}${params.toString() ? `?${params.toString()}` : ''}`;
  window.location.href = `${API_BASE}${url}`;
};

/**
 * Send forgot password request
 */
export const forgotPassword = async (email) => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    let errorMessage = 'Failed to send password reset email';
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Reset password with token
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    let errorMessage = 'Failed to reset password';
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    }
    
    throw new Error(errorMessage);
  }
};
